import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { confirmDestructive } from '@client/components/_ui/feedback/modal';
import { FilterSchema } from '@client/components/filters/core/schema';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { ReportFilters } from '@client/components/reports/ReportFilters';
import { buildInitialReportWhere } from '@client/components/reports/report-filter-config';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { DEEP_LIST_OFFSET_LIMIT, DEFAULT_PAGE_SIZE, MAX_REPORT_BULK_DELETE_IDS } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { BarChart3 } from 'lucide-react';
import { useEffect } from 'react';
import { z } from 'zod';
import { useReportTableColumns } from './columns';
import { ReportTableEmptyState } from './ReportTableEmptyState';

const ReportSearchSchema = z.object({
    'report-filters': z.array(FilterSchema).optional().catch(undefined),
});

export const Route = createFileRoute('/console/$orgId/reporting')({
    staticData: {
        ui: {
            title: 'Reporting',
            subtitle: 'Generate reports for your organisation',
            icon: BarChart3,
        },
        breadcrumb: {
            title: 'Reporting',
        },
        roles: ['user', 'shared_edit_user', 'org_admin', 'training_manager'],
        modules: ['claim', 'compliance', 'risk'],
        restrictToEmployee: false,
    },
    validateSearch: zodValidator(ReportSearchSchema),
    component: Reports,
});

type QueryParams = RouterInputs['report']['listReports'];

function Reports() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const searchParams = Route.useSearch();

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
        where: buildInitialReportWhere(searchParams['report-filters']),
    });
    const hasActiveFilters = !isWhereClauseEmpty(queryParams.where);

    const { data, error, isError, isLoading, isSuccess, refetch } = trpc.report.listReports.useQuery(queryParams);

    useEffect(() => {
        if (isSuccess && data.rows.length === 0 && data.total > 0 && (queryParams.offset ?? 0) > 0) {
            setQueryParams((previous) => ({ ...previous, offset: 0 }));
        }
    }, [data, isSuccess, queryParams.offset, setQueryParams]);

    const { mutateAsync: deleteReports, isPending: isDeletingReports } = trpc.report.deleteReports.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const columns = useReportTableColumns();

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isLoading,
        columns,
        rowKey: (row) => String(row.id),
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            maxAccessibleRecords: DEEP_LIST_OFFSET_LIMIT,
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        showSorterTooltip: false,
        emptyContent:
            isSuccess && data.total === 0 ? (
                <ReportTableEmptyState hasActiveFilters={hasActiveFilters} orgId={orgId} />
            ) : undefined,
        emptyState: { noRecordsTitle: 'No reports yet' },
    });

    const handleDeleteSelectedReports = () => {
        const selectedRowKeys = tableProps.rowSelection?.selectedRowKeys || [];
        const reportCount = selectedRowKeys.length;

        confirmDestructive({
            title: 'Delete selected reports',
            content: (
                <div>
                    <p>
                        Are you sure you want to delete <strong>{reportCount}</strong> selected{' '}
                        {reportCount === 1 ? 'report' : 'reports'}?
                    </p>
                    <p className="mt-2 text-danger-foreground">This action cannot be undone.</p>
                </div>
            ),
            okText: 'Delete',
            cancelText: 'Cancel',
            onOk: async () => {
                const ids = selectedRowKeys.map((key) => BigInt(key as string));
                let deletedCount = 0;
                try {
                    // The server bounds a single call — chunk so a selection
                    // preserved across pages deletes fully instead of failing outright.
                    for (let index = 0; index < ids.length; index += MAX_REPORT_BULK_DELETE_IDS) {
                        const result = await deleteReports({
                            ids: ids.slice(index, index + MAX_REPORT_BULK_DELETE_IDS),
                        });
                        deletedCount += result.deletedCount;
                    }
                } catch (error) {
                    // `mutateAsync` has already surfaced the failure through `onError`. Once an
                    // earlier chunk succeeded, resolve this callback so the modal closes instead
                    // of offering a retry with the now-stale captured selection.
                    if (deletedCount === 0) throw error;
                } finally {
                    // A mid-batch failure was already surfaced by onError; still report
                    // and refresh whatever the earlier chunks deleted.
                    if (deletedCount > 0) {
                        tableProps.clearSelection();
                        void trpcUtils.report.listReports.invalidate();
                        message.success(
                            `Successfully deleted ${deletedCount} ${deletedCount === 1 ? 'report' : 'reports'}.`,
                        );
                    }
                }
            },
        });
    };

    const selectedRowKeys = tableProps.rowSelection?.selectedRowKeys || [];

    return (
        <PageController route={Route}>
            <TableToolbar
                filters={
                    <ReportFilters
                        urlKey="report-filters"
                        initialFilters={searchParams['report-filters']}
                        onChange={(where) => {
                            setQueryParams((prev) => ({ ...prev, where, offset: 0 }));
                        }}
                    />
                }
                actions={
                    <>
                        <Button.Selection
                            danger
                            selectedCount={selectedRowKeys.length}
                            action="Delete"
                            noun="reports"
                            nounSingular="report"
                            loading={isDeletingReports}
                            onClick={handleDeleteSelectedReports}
                        />
                        <Button.Add.Link
                            to="/console/$orgId/reporting/add"
                            params={{ orgId }}
                            variant="primary"
                            label="Generate report"
                        />
                    </>
                }
            />

            <Outlet />

            {isError ? (
                <InlineQueryError error={error} onRetry={() => void refetch()} />
            ) : (
                <PrismaTable {...tableProps} />
            )}
        </PageController>
    );
}
